import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "./common/Icon";
import { Avatar, Dropdown } from "flowbite-react";
import {
  isImpersonating,
  permissionChecker,
  removeToken,
} from "../utils/permissions";
import { useApplication } from "../contexts/ApplicationContext";
import { useAsyncQueries } from "../contexts/AsyncQueryContext";
import {
  calculateExpirationPeriodTest,
  formatToReal,
  getUserDisplayName,
} from "../utils/utils";
import axios from "../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import {
  ADMIN,
  MONITORED_CLIENT,
  NEGATIVE,
  NOTIFICATIONS,
  PAYMENTS,
} from "../config/apiConfig";
import {
  IoIosArrowBack,
  IoIosArrowDown,
  IoIosArrowForward,
  IoMdRefresh,
} from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import {
  HiChevronDown,
  HiOutlineArrowRightOnRectangle,
  HiOutlineDocumentText,
  HiOutlineExclamationTriangle,
  HiOutlineShieldCheck,
  HiOutlineSparkles,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import ModalAddCredit from "./modals/ModalAddCredit";
import { ModalContract } from "./modals/ModalContract";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import NotificationCenter from "./common/NotificationCenter";
import ImportantNotificationsModal from "./common/ImportantNotificationsModal";
import { isVehicleOnlyHost } from "../utils/whiteLabel";
import { CREDIT_TONES, getCreditAlert } from "../utils/creditBalance";
import {
  DEFAULT_PROVIDER_MENU,
  DEFAULT_SELLER_MENU,
} from "../config/menuCatalog";
import useMenuLayout from "../hooks/useMenuLayout";

/* ===== White-label só de consulta veicular =====
   Crédito e cadastral saem do menu e o item "Consulte aqui" deixa de abrir
   submenu: vira link direto para as consultas veiculares. Vale também para os
   filhos do Administrativo que são de outro produto (carteira e negativação). */
const VEHICLE_ONLY_HIDDEN_LINKS = new Set([
  "/queries/credits",
  "/queries/cadastrals",
  "/admin/monitored-clients",
  "/admin/negatives",
]);

/* Itens de topo que não fazem parte do produto veicular. */
const VEHICLE_ONLY_HIDDEN_MENUS = new Set([
  "/gerenciamento-de-carteira",
  "/negatives",
  "/localize",
  "/credit-engine",
]);

const toVehicleOnlyMenu = (links) =>
  links
    .filter((item) => !VEHICLE_ONLY_HIDDEN_MENUS.has(item.link))
    .map((item) => {
      if (item.link === "/queries") {
        return {
          ...item,
          link: "/queries/autos",
          isCollapsible: false,
          children: [],
        };
      }
      if (!item.children?.length) return item;
      return {
        ...item,
        children: item.children.filter(
          (child) => !VEHICLE_ONLY_HIDDEN_LINKS.has(child.link),
        ),
      };
    });

/* Item que ganha o destaque animado no menu — é a porta de entrada do produto
   e o que a maioria vem fazer. Vai por nome porque o link muda no white-label
   veicular ("/queries" vira "/queries/autos"), e o perfil SELLER chama o mesmo
   item de "Consultas". */
const FEATURED_MENU_NAMES = new Set(["Consulte aqui", "Consultas"]);

/* ===== Agrupamento visual do menu (rótulos de seção) ===== */
const MENU_SECTIONS = {
  "Visão Geral": "Principal",
  Dashboard: "Principal",
  "Consulte aqui": "Principal",
  Consultas: "Principal",
  "Decida com segurança": "Principal",
  "Negative Clientes": "Gestão",
  "Gerenciamento de carteira": "Gestão",
  "Monitore Clientes": "Gestão",
  "Localizador PF/PJ": "Gestão",
  "Cobre seus clientes": "Gestão",
  Relatórios: "Gestão",
  "Meus Clientes": "Gestão",
  "Minhas Faturas": "Conta",
  Usuários: "Conta",
  "Dúvidas Frequentes": "Suporte",
  /* "Suporte" era o único item da seção de mesmo nome, logo depois de
     Usuários — o rótulo repetia a linha abaixo dele e quebrava "Conta" em
     duas. Abrir chamado é da conta, então sobe para lá; o agrupamento só
     funciona porque ele já vem contíguo a Minhas Faturas e Usuários na
     ordem do menu. */
  Suporte: "Conta",
  Administrativo: "Administração",
};

/* Item de menu criado pelo representante pode apontar para fora do painel. O
   <Link> do router só sabe navegar por rota interna, então link externo vira
   uma âncora comum — com rel="noopener" porque target="_blank" sem ele dá à
   página aberta acesso ao window.opener. */
/* Esqueleto do menu.
 *
 * A largura de cada barra varia porque um bloco de barras idênticas lê como
 * "quebrou", não como "carregando" — a irregularidade é o que faz o olho
 * reconhecer uma lista de rótulos. O ciclo é o mesmo para todas as linhas, sem
 * atraso escalonado: numa lista curta, a onda chama mais atenção que o menu.
 *
 * `aria-hidden` porque não há informação aqui; quem usa leitor de tela recebe o
 * aviso pelo `role="status"` do contêiner. */
const MENU_SKELETON_WIDTHS = [
  "68%",
  "82%",
  "55%",
  "74%",
  "61%",
  "88%",
  "50%",
  "70%",
];

const MenuSkeleton = ({ isCollapsed }) => (
  <div
    role="status"
    aria-label="Carregando o menu"
    className="flex flex-col gap-0.5"
  >
    {MENU_SKELETON_WIDTHS.map((width, index) => (
      <div
        key={index}
        aria-hidden="true"
        className={`flex items-center rounded-lg py-2.5 ${
          isCollapsed ? "justify-center px-2" : "gap-3 px-3"
        }`}
      >
        <span className="h-5 w-5 shrink-0 animate-pulse rounded-md bg-white/10" />
        {!isCollapsed && (
          <span
            className="h-3 animate-pulse rounded bg-white/10"
            style={{ width }}
          />
        )}
      </div>
    ))}
  </div>
);

/* Cores do card de saldo por tom. A sidebar é escura em todos os temas, então
   os valores são fixos — mas o alarme não se apoia só na cor: cada tom também
   muda ícone, rótulo, texto e destaque do botão. É o que mantém o aviso legível
   nos temas cuja paleta de sidebar já é vermelha ou âmbar, e para quem não
   distingue as cores.

   `convite` usa o accent do tema em vez de uma cor de alerta: quem nunca
   recarregou não tem um problema para resolver, tem um passo para dar. */
const CREDIT_TONE_STYLES = {
  [CREDIT_TONES.CRITICO]: {
    borda: "border-red-400/50",
    fundo:
      "linear-gradient(135deg, rgba(220,38,38,0.42) 0%, rgba(220,38,38,0.10) 100%)",
    rotulo: "text-red-200",
    valor: "text-red-100",
    texto: "text-red-100/80",
    botao: "bg-red-500 hover:bg-red-400 text-white",
    recolhido: "bg-red-500/25 text-red-200 ring-1 ring-red-400/60",
    ponto: "bg-red-400 animate-pulse",
    pulsa: true,
  },
  [CREDIT_TONES.ATENCAO]: {
    borda: "border-amber-400/50",
    fundo:
      "linear-gradient(135deg, rgba(217,119,6,0.40) 0%, rgba(217,119,6,0.10) 100%)",
    rotulo: "text-amber-200",
    valor: "text-amber-100",
    texto: "text-amber-100/80",
    botao: "bg-amber-500 hover:bg-amber-400 text-amber-950",
    recolhido: "bg-amber-500/25 text-amber-200 ring-1 ring-amber-400/60",
    ponto: "bg-amber-400",
    pulsa: false,
  },
  [CREDIT_TONES.CONVITE]: {
    borda: "border-[color:var(--menu-accent)]/45",
    fundo:
      "linear-gradient(135deg, var(--sidebar-credit-from) 0%, var(--sidebar-credit-to) 100%)",
    rotulo: "menu-accent-text-soft",
    valor: "text-white",
    texto: "text-white/70",
    botao: "menu-accent-surface",
    recolhido:
      "menu-accent-tint menu-accent-text-soft ring-1 ring-[color:var(--menu-accent)]/45",
    ponto: "bg-[color:var(--menu-accent)]",
    pulsa: false,
  },
};

const MenuLink = ({ item, children, ...props }) =>
  item?.isExternal ? (
    <a href={item.link} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ) : (
    <Link to={item.link} {...props}>
      {children}
    </Link>
  );

const Sidebar = () => {
  const { user, userPermission, refetchUser, isLoadingUser } = useApplication();
  // Consulta assíncrona concluída abre no modal global, sem tirar o usuário da
  // tela em que ele está.
  const { openResult: openAsyncQueryResult } = useAsyncQueries();
  const queryClient = useQueryClient();
  const userDisplayName = getUserDisplayName(user);

  /* Papel do usuário em texto, para o cartão da conta. Sem isto a linha de
     apoio repetia o e-mail que já aparece no menu aberto. "ADMIN" é o admin da
     empresa do cliente, não da Relinq — daí "Administrador" e não "Suporte". */
  const ROTULO_PERFIL = {
    POWER_USER: "Equipe Relinq",
    ADMIN: "Administrador",
    SELLER: "Comercial",
    PROVIDER: "Provedor",
    USER: "Usuário",
  };
  const rotuloDoPerfil =
    ROTULO_PERFIL[
      Array.isArray(userPermission) ? userPermission[0] : userPermission
    ] || null;
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [titlePage, setTitlePage] = useState("Dashboard");
  const sidebarRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [isOpenModalContract, setIsOpenModalContract] = useState(false);
  const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const hoverCloseTimeoutRef = useRef(null);
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 640 : false,
  );
  const COLLAPSED_WIDTH = 75;
  const EXPANDED_WIDTH = 256;
  const isMobileView = !isDesktopView;
  const sidebarWidth = isMobileView
    ? EXPANDED_WIDTH
    : isCollapsed
      ? COLLAPSED_WIDTH
      : EXPANDED_WIDTH;
  const contentMarginLeft = isDesktopView ? sidebarWidth : 0;
  const contentWidth = isDesktopView
    ? `calc(100% - ${sidebarWidth}px)`
    : "100%";
  const isObahCredito =
    typeof window !== "undefined" &&
    window.location.href.includes("obahcredito");
  const isVehicleOnly = isVehicleOnlyHost();
  const isRepresentative = user?.Company?.companyTypeId === "REPRESENTANTE";
  const shouldHideObahMenuItems = isObahCredito && !isRepresentative;
  const obahHiddenMenuItems = new Set([
    "Minhas Faturas",
    "Usuários",
    "Documentação",
    "Suporte",
  ]);
  const clearHoverClose = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    clearHoverClose();
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setHoverRect(null);
    }, 180);
  }, [clearHoverClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const widthValue = isDesktopView ? `${sidebarWidth}px` : "0px";
    document.documentElement.style.setProperty("--sidebar-width", widthValue);
  }, [sidebarWidth, isDesktopView]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        isCollapsed ? "true" : "false",
      );
    } catch {
      // ignore write errors
    }
  }, [isCollapsed]);
  const daysLeft = calculateExpirationPeriodTest(
    user?.Company?.planExpirationDate,
  );
  const canSeeInvoices = permissionChecker(["ADMIN"], userPermission, user, [
    "ALL",
  ]);
  const isAdminUser = Array.isArray(userPermission)
    ? userPermission.includes("ADMIN")
    : userPermission === "ADMIN";
  const isPowerUser = Array.isArray(userPermission)
    ? userPermission.includes("POWER_USER")
    : userPermission === "POWER_USER";
  /* Acesso externo (o responsável por um provedor). Aparece aqui porque TODA
     contagem do menu — boletos, negativações, chamados, carteira, notificações
     — bate numa rota que esse perfil não pode chamar: sem o recorte, o sidebar
     dispararia uma sequência de 403 em toda navegação dele. */
  const isProviderUser = Array.isArray(userPermission)
    ? userPermission.includes("PROVIDER")
    : userPermission === "PROVIDER";
  // Notificação é por usuário, não por perfil: todo usuário autenticado vê o
  // sino. O que cada um recebe é decidido pelo targetType da notificação
  // (ALL / COMPANY / USER / PERMISSION / COMPANY_TYPE), no backend.
  // A gestão (criar/editar/excluir) segue restrita a POWER_USER.
  const canSeeNotificationCenter = Boolean(user?.id) && !isProviderUser;
  const canSeeNegativesAdmin =
    ((isAdminUser || isPowerUser) && Number(user?.Company?.id) === 1) ||
    (isAdminUser && isRepresentative);
  /* Localizador é liberado por empresa, não por perfil: vale a flag
     hasLocalizador do cliente, editável no formulário de consultas da empresa.
     O backend aplica a mesma regra nas rotas /admin/cadastral/*.
     BOOLEAN do MySQL volta como 1/0, daí a coerção — mesma do
     hasClientMonitoring acima. */
  const canSeeLocalizador = [true, 1, "1", "true"].includes(
    user?.Company?.hasLocalizador,
  );
  /* Cobranças aparece para todo mundo de propósito: quem não tem a flag
     hasCharge cai na landing do produto, com o caminho para pedir a liberação.
     Quem não conhece a funcionalidade não iria procurá-la. */
  /* A fila de aprovação é da matriz: é ela que confirma o envio e, com isso,
     debita o cliente. O backend aplica o mesmo recorte em /admin/debt-collections
     — aqui é só para o item não aparecer para quem levaria 403. */
  const canSeeCobrancasAdmin =
    (isAdminUser || isPowerUser) && Number(user?.Company?.id) === 1;
  /* "Decida com segurança" (motor de crédito) aparece para todo mundo, como
     Cobranças: quem não tem nenhuma consulta habilitada para o motor
     (hasCreditEngine, calculado pelo /user-current a partir do catálogo) cai na
     landing do produto em /credit-engine, com o caminho para pedir a liberação.
     Esconder o item deixava a funcionalidade invisível justamente para quem
     ainda não a contratou. As rotas /credit-engine/config seguem protegidas no
     backend. */
  const canSeeContract = isAdminUser;

  /* Cadastrar representantes é liberado empresa a empresa
     (Companies.canCreateRepresentatives), definido por quem está acima na
     hierarquia. A Relinq (empresa 1) sempre enxerga. Sem a flag, o item some
     do menu — e a rota POST /admin/representative/new devolve 403, então
     esconder aqui não é a única defesa. */
  const canSeeRepresentativesAdmin =
    Number(user?.Company?.id) === 1 ||
    [true, 1, "1", "true"].includes(user?.Company?.canCreateRepresentatives);

  /* Um filho do menu aparece se o perfil permitir e se o recurso estiver
     liberado para a empresa. Fica centralizado aqui porque o submenu é
     renderizado em dois lugares (recolhido e expandido) e as duas listas
     precisam concordar. */
  const isChildVisible = (child, parent) => {
    if (child.link === "/admin/cobrancas" && !canSeeCobrancasAdmin)
      return false;
    if (child.link === "/admin/representatives" && !canSeeRepresentativesAdmin)
      return false;
    return permissionChecker(
      child.permission,
      userPermission,
      user,
      child.typeCompany ?? parent?.typeCompany,
    );
  };

  const coercePaid = (isPaid) =>
    isPaid === true || isPaid === 1 || isPaid === "1";
  const hasClientMonitoringAccess =
    user?.Company?.hasClientMonitoring === true ||
    user?.Company?.hasClientMonitoring === 1 ||
    user?.Company?.hasClientMonitoring === "1" ||
    user?.Company?.hasClientMonitoring === "true" ||
    isAdminUser ||
    isPowerUser;
  const canQueryMonitoredClients = hasClientMonitoringAccess && !isProviderUser;

  const { data: boletoPendingCount = 0 } = useQuery(
    ["sidebar-boleto-notification", user?.Company?.id],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?isPaid=false&itemsPerPage=-1`,
      );
      const rows = data?.data?.data || [];
      return rows.filter(
        (inv) => inv?.type === "BOLETO" && !coercePaid(inv?.isPaid),
      ).length;
    },
    {
      enabled: Boolean(canSeeInvoices && user?.Company?.id),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: negativePendingCount = 0 } = useQuery(
    ["sidebar-negative-pending", user?.Company?.id],
    async () => {
      // A aba "A fazer" da gestão: o backend recorta pela carteira de quem
      // pede (a Relinq vê tudo) e põe ali só o que essa pessoa resolve.
      const { data } = await axios.get(NEGATIVE.MANAGEMENT, {
        params: { group: "afazer", page: 1, pageSize: 1 },
      });
      return Number(data?.total) || 0;
    },
    {
      enabled: Boolean(canSeeNegativesAdmin),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  // O escopo do count vem do backend: POWER_USER recebe os pendentes do
  // sistema inteiro; ADMIN representante, apenas os da própria carteira.
  const canSeeAdminTickets =
    isPowerUser ||
    (isAdminUser &&
      ["REPRESENTANTE", "ADMIN"].includes(user?.Company?.companyTypeId));

  const { data: ticketsPendingCount = 0 } = useQuery(
    ["sidebar-tickets-pending", user?.Company?.id],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_TICKETS}?page=1&itemsPerPage=1`,
      );
      return Number(data?.data?.summary?.pending) || 0;
    },
    {
      enabled: Boolean(canSeeAdminTickets && user?.Company?.id),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: monitoredClientsForSidebar = [] } = useQuery(
    ["monitored-clients"],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.LIST);
      return data?.data || [];
    },
    {
      enabled: Boolean(canQueryMonitoredClients && user?.Company?.id),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const monitoredUpdatesCount = useMemo(
    () =>
      monitoredClientsForSidebar.reduce(
        (sum, client) => sum + Number(client?.unreadUpdatesCount || 0),
        0,
      ),
    [monitoredClientsForSidebar],
  );
  const monitoredUpdatesLabel =
    monitoredUpdatesCount > 99 ? "99+" : monitoredUpdatesCount;

  const { data: notificationsData, isLoading: isLoadingNotifications } =
    useQuery(
      ["system-notifications", user?.id],
      async () => {
        const { data } = await axios.get(NOTIFICATIONS.LIST, {
          params: { page: 1, limit: 50 },
        });
        return data?.data || { notifications: [], unreadCount: 0 };
      },
      {
        enabled: Boolean(user?.id && canSeeNotificationCenter),
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
      },
    );

  const systemNotifications = useMemo(
    () =>
      (notificationsData?.notifications || []).map((notification) => ({
        ...notification,
        type: String(notification.type || "system").toLowerCase(),
        label: notification.actionLabel,
      })),
    [notificationsData?.notifications],
  );

  const refreshNotifications = () =>
    queryClient.invalidateQueries(["system-notifications", user?.id]);

  const markNotificationAsRead = async (notificationId) => {
    await axios.put(NOTIFICATIONS.MARK_READ(notificationId));
    await refreshNotifications();
  };

  const markAllNotificationsAsRead = async () => {
    await axios.put(NOTIFICATIONS.MARK_ALL_READ);
    await refreshNotifications();
  };

  const handleToggleSidebar = (linkName = "") => {
    setIsOpen(!isOpen);
    if (linkName !== "") setTitlePage(linkName);
  };

  const toggleCollapse = () => {
    if (!isDesktopView) return;
    setIsCollapsed((prev) => !prev);
  };

  const collapsedTooltip = useMemo(() => {
    if (
      !isDesktopView ||
      !isCollapsed ||
      !hoveredMenu ||
      !hoverRect ||
      typeof document === "undefined"
    ) {
      return null;
    }

    const allowedChildren = (hoveredMenu.children || []).filter((child) =>
      isChildVisible(child, hoveredMenu),
    );

    if (!allowedChildren.length) return null;

    const style = {
      position: "fixed",
      top: hoverRect.top,
      left: hoverRect.right + 8,
      width: 220,
      zIndex: 9999,
    };

    return createPortal(
      <div
        className="rounded-xl border border-white/10 shadow-2xl shadow-black/60 pointer-events-auto overflow-hidden"
        style={{ ...style, background: "var(--sidebar-popover)" }}
        onMouseEnter={clearHoverClose}
        onMouseLeave={scheduleHoverClose}
      >
        <p className="px-3.5 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/30">
          {hoveredMenu.name}
        </p>
        <div className="flex flex-col p-1.5 pt-0.5 gap-0.5">
          {allowedChildren.map((child, idx) => {
            const isChildActive = location.pathname === child.link;
            const showNegativesPending =
              child.link === "/admin/negatives" && negativePendingCount > 0;
            const showTicketsPending =
              child.link === "/admin/support" && ticketsPendingCount > 0;
            return (
              <MenuLink
                item={child}
                key={idx}
                onClick={() => {
                  handleToggleSidebar(formatTitle(child.name));
                  setHoveredMenu(null);
                }}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] transition-colors ${
                  isChildActive
                    ? "menu-accent-text-soft menu-accent-tint font-semibold"
                    : "text-white/60 hover:text-white hover:bg-white/[0.06]"
                }`}
              >
                {child.icon && (
                  <Icon className="text-[16px] shrink-0">{child.icon}</Icon>
                )}
                <span className="truncate">{child.name}</span>
                {showNegativesPending && (
                  <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                    {negativePendingCount}
                  </span>
                )}
                {showTicketsPending && (
                  <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
                    {ticketsPendingCount}
                  </span>
                )}
              </MenuLink>
            );
          })}
        </div>
      </div>,
      document.body,
    );
  }, [
    isCollapsed,
    hoveredMenu,
    hoverRect,
    userPermission,
    user,
    location.pathname,
    clearHoverClose,
    scheduleHoverClose,
    isDesktopView,
    negativePendingCount,
    ticketsPendingCount,
  ]);

  useEffect(() => {
    if (!isDesktopView && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isDesktopView, isCollapsed]);

  const toggleMenu = (menuName) => {
    setExpandedMenus((prev) => {
      const isAlreadyExpanded = prev[menuName];
      if (isAlreadyExpanded) return {};
      return { [menuName]: true };
    });
  };

  const handleRefresh = () => {
    refetchUser();
  };

  /* Estado do saldo pré-pago: "ok", "baixo" (abaixo de R$ 5) ou "zerado"
     (zero ou negativo). Só o pré-pago tem saldo a zerar, e o bloco do saldo já
     está atrás dessa condição — mas o cálculo fica aqui fora para o JSX não
     repetir a comparação em cada um dos oito lugares que reagem a ela. */
  /* Representante recebe outra mensagem (o saldo dele sustenta as consultas de
     toda a sub-rede, então ficar sem saldo bloqueia os CLIENTES dele), e quem
     nunca recarregou recebe convite em vez de alarme. */
  const creditAlert = getCreditAlert(user?.Company?.credit, {
    isRepresentative,
    hasRecharged: user?.Company?.hasRecharged,
  });
  const creditTone = creditAlert ? CREDIT_TONE_STYLES[creditAlert.tone] : null;

  /* ===== helper de título ===== */
  const formatTitle = (name = "") => {
    const n = (name || "").toLowerCase();
    if (n.includes("crédito") || n === "créditos")
      return "Consultas de crédito";
    if (n.includes("cadastra") || n === "cadastrais")
      return "Consultas cadastrais";
    if (n.includes("veicular") || n === "veiculares")
      return "Consultas veiculares";
    return name || "Visão Geral";
  };

  const getTitleFromPath = (pathname) => {
    if (pathname.startsWith("/queries/credits")) return "Consultas de crédito";
    if (pathname.startsWith("/queries/cadastrals"))
      return "Consultas cadastrais";
    if (pathname.startsWith("/queries/autos")) return "Consultas veiculares";
    return (
      menuLinks.find((link) => link.link === pathname)?.name || "Visão Geral"
    );
  };

  /* ===== PERFIL ===== */
  const isSeller = Array.isArray(userPermission)
    ? userPermission.includes("SELLER")
    : userPermission === "SELLER";

  /* ===== MENU BASE (não SELLER) ===== */
  /* Menu do perfil padrão: o catálogo, reorganizado pelo layout que o
     representante acima montou. Sem layout personalizado em lugar nenhum da
     cadeia, o hook devolve o próprio DEFAULT_MENU. */
  const { menuLinks: baseMenuLinks, isLoading: isLoadingMenu } = useMenuLayout(
    !isSeller && !isProviderUser && !!user?.id,
  );

  /* ===== MENU SELLER (sem o "Administrativo") ===== */
  const rawSellerMenu = DEFAULT_SELLER_MENU;

  /* === Dá permissão SELLER para todos os itens/filhos do menu SELLER === */
  const addSellerPermission = (item) => {
    const patched = {
      ...item,
      permission: Array.from(new Set([...(item.permission || []), "SELLER"])),
      typeCompany:
        item.typeCompany && item.typeCompany.length
          ? item.typeCompany
          : ["ALL"],
    };
    if (item.children && item.children.length) {
      patched.children = item.children.map(addSellerPermission);
    }
    return patched;
  };

  const sellerMenuLinks = useMemo(
    () => rawSellerMenu.map(addSellerPermission),
    [],
  );

  /* ===== Escolha final conforme perfil (+ recorte do domínio veicular) ===== */
  const profileMenuLinks = isProviderUser
    ? DEFAULT_PROVIDER_MENU
    : isSeller
      ? sellerMenuLinks
      : baseMenuLinks;
  const menuLinks = isVehicleOnly
    ? toVehicleOnlyMenu(profileMenuLinks)
    : profileMenuLinks;

  /* ===== Itens visíveis (permissões + regras white-label) ===== */
  /* Esqueleto enquanto o menu ainda não pode estar certo: sem o usuário, o
     permissionChecker recorta tudo e a lista apareceria vazia antes mesmo de a
     busca do layout começar. */
  const isMenuPending = isLoadingUser || isLoadingMenu;

  const visibleMenuLinks = menuLinks.filter((menuLink) => {
    if (shouldHideObahMenuItems && obahHiddenMenuItems.has(menuLink.name))
      return false;
    if (menuLink.link === "/admin/negatives" && !canSeeNegativesAdmin)
      return false;
    if (menuLink.link === "/localize" && !canSeeLocalizador) return false;

    if (
      !permissionChecker(
        menuLink.permission,
        userPermission,
        user,
        menuLink.typeCompany,
      )
    ) {
      return false;
    }

    /* Accordion cujos filhos foram todos recortados abriria vazio. Acontece
       principalmente em grupo montado por representante, que herda a permissão
       da união dos filhos: quem enxerga só um deles passaria no teste acima e
       veria um grupo sem nada dentro. */
    if (menuLink.isCollapsible) {
      return (menuLink.children || []).some((child) =>
        isChildVisible(child, menuLink),
      );
    }

    return true;
  });

  /* "Administração" (a seção) abria em "Administrativo" (o item), que por sua
     vez abria nos itens de verdade — o mesmo nome duas vezes e um clique a
     mais para chegar a qualquer coisa. Os filhos sobem para o nível do pai,
     carregando a seção dele: sem `section` no objeto, MENU_SECTIONS os
     procuraria pelo próprio nome ("Relatórios", "Review ⭐") e eles cairiam
     em seções erradas. */
  const ITEM_REDUNDANTE = "Administrativo";

  const menuLinksExibidos = visibleMenuLinks.flatMap((menuLink) => {
    if (menuLink.name !== ITEM_REDUNDANTE || !menuLink.isCollapsible) {
      return [menuLink];
    }

    const secao = MENU_SECTIONS[menuLink.name] || "Administração";
    return (menuLink.children || [])
      .filter((child) => isChildVisible(child, menuLink))
      .map((child) => ({
        ...child,
        // herdado do pai achatado, para o permissionChecker seguir igual
        typeCompany: child.typeCompany ?? menuLink.typeCompany,
        section: secao,
      }));
  });

  const closeSidebar = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsDesktopView(window.innerWidth >= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !menuBtnRef.current.contains(event.target) &&
        isOpen
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [location.pathname, isOpen]);

  useEffect(() => {
    setTitlePage(formatTitle(getTitleFromPath(location.pathname)));
  }, [location.pathname, menuLinks]);

  // Fecha o menu (e o backdrop) sempre que a rota muda — no mobile o overlay
  // preto ficava preso quando o toggle não chegava a virar `false`.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Trava o scroll da página enquanto o drawer mobile está aberto. Sem isso, o
  // gesto de rolar dentro do menu (ou sobre o backdrop) passa para o conteúdo
  // atrás. `position: fixed` é necessário no iOS, onde `overflow: hidden` no
  // body não segura o scroll por toque — por isso guardamos e restauramos a
  // posição, senão a página volta para o topo ao fechar o menu.
  useEffect(() => {
    if (!isOpen || isDesktopView) return;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, isDesktopView]);

  let logoSrc;

  switch (true) {
    case window.location.href.includes("acessopositivo"):
      logoSrc = "/assets/white-label/acesso-positivo/logo_acesso.png";
      break;
    case window.location.href.includes("periciadecredito"):
      logoSrc = "/assets/white-label/pericia-de-credito/logo_branco.png";
      break;
    case window.location.href.includes("meuraioxdecredito"):
      logoSrc = "/assets/white-label/raiox-de-credito/logo_branco.png";
      break;
    case window.location.href.includes("solucoesmoura"):
      logoSrc = "/assets/white-label/solucoes-moura/logo_branco.png";
      break;
    case window.location.href.includes("restricar"):
      logoSrc = "/assets/white-label/restricar/logo_branco.png";
      break;
    default:
      logoSrc = "/assets/logo-branco.png";
      break;
  }

  // Logo personalizada do cliente (fundo escuro) sobrescreve a padrão
  // white-label apenas quando enviada.
  const finalLogoSrc = user?.Company?.logoWhiteUrl || logoSrc;

  return (
    <>
      {/* ── Backdrop (mobile) ── */}
      <div
        onClick={closeSidebar}
        aria-hidden="true"
        className={`fixed inset-0 z-30 bg-black/50 transition-opacity duration-300 ease-in-out sm:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* ── Sidebar ── */}
      <aside
        id="logo-sidebar"
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-40 h-screen sm:translate-x-0 sm:shadow-none ${
          isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        }`}
        aria-label="Sidebar"
        style={{
          width: `${sidebarWidth}px`,
          // No mobile, 100vh (do `h-screen`) inclui a área atrás da barra de
          // endereço, deixando o menu mais alto que a tela visível — o rodapé
          // fica inalcançável e a lista não rola. 100dvh acompanha a altura
          // real. Navegadores sem suporte descartam a linha e caem no
          // `h-screen` da classe.
          height: "100dvh",
          transition:
            "width 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* ── Botão de recolher (flutuante na borda) ── */}
        <button
          onClick={toggleCollapse}
          title={isCollapsed ? "Expandir menu" : "Recolher menu"}
          aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
          className="hidden sm:flex absolute -right-3 top-7 z-50 h-6 w-6 items-center justify-center rounded-full border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition-colors"
          style={{
            background: "var(--sidebar-button)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.45)",
            marginTop: "-10px",
          }}
        >
          {isCollapsed ? (
            <IoIosArrowForward size={12} />
          ) : (
            <IoIosArrowBack size={12} />
          )}
        </button>

        <div className="h-full flex flex-col">
          {/* ── Logo ── */}
          <div
            className={`flex items-center justify-center shrink-0 ${
              isCollapsed ? "h-16 px-2" : "h-20 px-5"
            }`}
          >
            <img
              src={finalLogoSrc}
              alt="Logo"
              className="object-contain max-w-full"
              style={{
                height: isCollapsed ? "22px" : "42px",
                transition: "height 0.25s ease",
              }}
            />
          </div>

          {/* ── Separator ── */}
          <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {/* ── Credit balance (PRE) ──
               Saldo curto não pode ficar com a mesma cara de saldo saudável: o
               cliente só descobria o problema quando a consulta voltava 402. Os
               três estados vêm de utils/creditBalance, que é a mesma régua usada
               no Dashboard.

               O alarme não se apoia só na cor: o card ganha borda, ícone, uma
               frase dizendo o que fazer e o botão em destaque. É o que mantém o
               aviso legível nos temas cuja paleta de sidebar já é vermelha ou
               âmbar, onde só trocar o gradiente não mudaria nada. */}
          {user?.Company?.typePayment === "PRE" && (
            <div
              className={`mx-3 mt-3 mb-1 ${isCollapsed ? "flex flex-col items-center gap-2" : ""}`}
            >
              {!isCollapsed ? (
                <div
                  className={`rounded-xl border p-3 transition-colors ${
                    creditTone ? creditTone.borda : "border-white/[0.08]"
                  }`}
                  style={{
                    background: creditTone
                      ? creditTone.fundo
                      : "linear-gradient(135deg, var(--sidebar-credit-from) 0%, var(--sidebar-credit-to) 100%)",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p
                      className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                        creditTone ? creditTone.rotulo : "text-white/40"
                      }`}
                    >
                      {creditAlert && (
                        <span className="relative flex items-center">
                          {/* Convite não leva triângulo de alerta: ali não há
                              nada errado, há uma conta para começar. */}
                          {creditAlert.tone === CREDIT_TONES.CONVITE ? (
                            <HiOutlineSparkles size={13} />
                          ) : (
                            <HiOutlineExclamationTriangle size={13} />
                          )}
                          {creditTone.pulsa && (
                            <span
                              className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${creditTone.ponto}`}
                            />
                          )}
                        </span>
                      )}
                      {creditAlert ? creditAlert.titulo : "Saldo disponível"}
                    </p>
                    <button
                      data-tooltip-id="tooltip-refresh-credit"
                      onClick={handleRefresh}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition"
                    >
                      <IoMdRefresh size={13} />
                    </button>
                  </div>
                  {/* Estado normal: valor e botão na mesma linha, que é o
                      formato compacto de sempre. Em alerta o bloco empilha —
                      valor, motivo, e o botão em largura cheia embaixo — porque
                      aí ele deixa de ser um atalho e passa a ser a única coisa
                      que resolve o problema. */}
                  <div
                    className={
                      creditAlert
                        ? ""
                        : "flex items-center justify-between gap-2"
                    }
                  >
                    <span
                      className={`block text-[15px] font-bold tabular-nums truncate ${
                        creditTone ? creditTone.valor : "text-white"
                      }`}
                    >
                      {formatToReal(user?.Company?.credit || 0)}
                    </span>

                    {creditAlert && (
                      <p
                        className={`mt-1.5 text-[10.5px] leading-snug ${creditTone.texto}`}
                      >
                        {creditAlert.mensagem}
                      </p>
                    )}

                    <button
                      data-tooltip-id="tooltip-add-credit"
                      onClick={() => setIsOpenModalAddCredit(true)}
                      className={`flex items-center gap-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                        creditAlert
                          ? "mt-2.5 w-full justify-center px-2.5 py-2"
                          : "shrink-0 px-2.5 py-1.5"
                      } ${creditTone ? creditTone.botao : "menu-accent-surface"}`}
                    >
                      <MdOutlinePayments size={13} />
                      {creditAlert ? creditAlert.acao : "Adicionar"}
                    </button>
                  </div>
                </div>
              ) : (
                /* Recolhido sobra um botão de 36px. O aviso tem que caber nele,
                   senão colapsar a sidebar esconde o problema: daí o anel
                   colorido e o ponto no canto. */
                <button
                  data-tooltip-id="tooltip-add-credit"
                  onClick={() => setIsOpenModalAddCredit(true)}
                  className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                    creditTone
                      ? creditTone.recolhido
                      : "menu-accent-tint menu-accent-text-soft"
                  }`}
                >
                  <MdOutlinePayments size={16} />
                  {creditAlert && (
                    <span
                      className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-[color:var(--sidebar-button)] ${creditTone.ponto}`}
                    />
                  )}
                </button>
              )}
              <Tooltip
                id="tooltip-add-credit"
                content={
                  creditAlert
                    ? `${creditAlert.curto} — ${creditAlert.acao.toLowerCase()}`
                    : "Adicionar crédito"
                }
              />
              <Tooltip id="tooltip-refresh-credit" content="Atualizar saldo" />
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain no-scrollbar py-3 px-2">
            {daysLeft > 0 && isMenuPending && (
              <MenuSkeleton isCollapsed={isCollapsed} />
            )}

            {daysLeft > 0 && !isMenuPending && (
              <nav className="flex flex-col gap-0.5">
                {menuLinksExibidos.map((menuLink, index) => {
                  const isActive = location.pathname === menuLink.link;
                  const isExpanded = expandedMenus[menuLink.name];
                  const hasChildren =
                    Array.isArray(menuLink.children) &&
                    menuLink.children.length > 0;
                  const showBoletoNotification =
                    menuLink.name === "Minhas Faturas" &&
                    boletoPendingCount > 0;
                  const showMonitoredUpdates =
                    menuLink.link === "/monitore-clientes" &&
                    monitoredUpdatesCount > 0;
                  const isChildActive = hasChildren
                    ? (menuLink.children || []).some(
                        (c) => location.pathname === c.link,
                      )
                    : false;
                  const itemActive = isActive || isChildActive;
                  /* Já ativo o item tem o próprio realce; manter os dois
                     empilhados só deixaria o menu inquieto sem informar nada. */
                  const isFeatured =
                    FEATURED_MENU_NAMES.has(menuLink.name) && !itemActive;
                  const section =
                    menuLink.section || MENU_SECTIONS[menuLink.name] || "Menu";
                  const prevSection =
                    index > 0
                      ? menuLinksExibidos[index - 1].section ||
                        MENU_SECTIONS[menuLinksExibidos[index - 1].name] ||
                        "Menu"
                      : null;
                  const showSectionHeading = section !== prevSection;
                  const activeStyle = {
                    background:
                      "linear-gradient(90deg, var(--menu-accent-soft) 0%, var(--menu-accent-soft-fade) 100%)",
                    boxShadow: "inset 0 0 0 1px var(--menu-accent-ring)",
                  };

                  return (
                    <div key={index}>
                      {showSectionHeading &&
                        (isCollapsed ? (
                          index > 0 && (
                            <div className="mx-2 my-2 h-px bg-white/[0.07]" />
                          )
                        ) : (
                          <p
                            className={`px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/25 ${
                              index === 0 ? "pt-1" : "pt-4"
                            }`}
                          >
                            {section}
                          </p>
                        ))}
                      {!menuLink.isCollapsible ? (
                        <MenuLink
                          item={menuLink}
                          onClick={() =>
                            handleToggleSidebar(formatTitle(menuLink.name))
                          }
                          title={isCollapsed ? menuLink.name : undefined}
                          className={`relative group flex items-center rounded-lg py-2.5 transition-all duration-150 ${
                            isCollapsed ? "justify-center px-2" : "gap-3 px-3"
                          } ${
                            isActive
                              ? "text-white"
                              : "text-white/50 hover:text-white/90 hover:bg-white/[0.05]"
                          } ${
                            isFeatured
                              ? `menu-featured-item ${isCollapsed ? "" : "menu-featured-item-inline"}`
                              : ""
                          }`}
                          style={isActive ? activeStyle : {}}
                        >
                          {isActive && !isCollapsed && (
                            <span className="menu-accent-bar absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full" />
                          )}
                          {isFeatured ? (
                            <span className="menu-featured-tile">
                              <Icon className="text-[16px] shrink-0">
                                {menuLink.icon}
                              </Icon>
                            </span>
                          ) : (
                            <Icon
                              className={`text-[19px] shrink-0 transition-colors ${
                                isActive
                                  ? "menu-accent-text"
                                  : "text-white/40 group-hover:text-white/70"
                              }`}
                            >
                              {menuLink.icon}
                            </Icon>
                          )}
                          {!isCollapsed && (
                            <span className="text-[13px] font-medium leading-none flex-1 truncate">
                              {menuLink.name}
                            </span>
                          )}
                          {showBoletoNotification && (
                            <span
                              className={`inline-flex ${
                                isCollapsed
                                  ? "absolute top-1.5 right-1"
                                  : "ml-auto"
                              }`}
                            >
                              <span className="absolute inline-flex h-2 w-2 rounded-full bg-rose-400 opacity-75 animate-ping" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
                            </span>
                          )}
                          {showMonitoredUpdates && (
                            <span
                              className={`menu-accent-surface flex items-center justify-center rounded-full text-[10px] font-bold shadow-sm shadow-black/30 ${
                                isCollapsed
                                  ? "absolute -top-0.5 right-0 h-4 min-w-[18px] px-1"
                                  : "ml-auto h-5 min-w-[22px] px-1.5"
                              }`}
                            >
                              {monitoredUpdatesLabel}
                            </span>
                          )}
                        </MenuLink>
                      ) : (
                        <div
                          className="relative"
                          onMouseEnter={(event) => {
                            if (!isCollapsed) return;
                            clearHoverClose();
                            setHoveredMenu(menuLink);
                            setHoverRect(
                              event.currentTarget.getBoundingClientRect(),
                            );
                          }}
                          onMouseMove={(event) => {
                            if (!isCollapsed) return;
                            setHoverRect(
                              event.currentTarget.getBoundingClientRect(),
                            );
                          }}
                          onMouseLeave={() => {
                            if (!isCollapsed) return;
                            scheduleHoverClose();
                          }}
                        >
                          <button
                            onClick={() => toggleMenu(menuLink.name)}
                            title={isCollapsed ? menuLink.name : undefined}
                            className={`relative group flex items-center w-full rounded-lg py-2.5 transition-all duration-150 ${
                              isCollapsed
                                ? "justify-center px-2"
                                : "gap-3 px-3 justify-between"
                            } ${
                              itemActive
                                ? "text-white"
                                : "text-white/50 hover:text-white/90 hover:bg-white/[0.05]"
                            } ${
                              isFeatured
                                ? `menu-featured-item ${isCollapsed ? "" : "menu-featured-item-inline"}`
                                : ""
                            }`}
                            style={itemActive ? activeStyle : {}}
                          >
                            {itemActive && !isCollapsed && (
                              <span className="menu-accent-bar absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full" />
                            )}
                            <div
                              className={`flex items-center min-w-0 ${
                                isCollapsed
                                  ? ""
                                  : isFeatured
                                    ? "gap-2"
                                    : "gap-3"
                              }`}
                            >
                              {isFeatured ? (
                                <span className="menu-featured-tile">
                                  <Icon className="text-[16px] shrink-0">
                                    {menuLink.icon}
                                  </Icon>
                                </span>
                              ) : (
                                <Icon
                                  className={`text-[19px] shrink-0 transition-colors ${
                                    itemActive
                                      ? "menu-accent-text"
                                      : "text-white/40 group-hover:text-white/70"
                                  }`}
                                >
                                  {menuLink.icon}
                                </Icon>
                              )}
                              {!isCollapsed && (
                                <span className="text-[13px] font-medium leading-none truncate">
                                  {menuLink.name}
                                </span>
                              )}
                            </div>
                            {!isCollapsed && (
                              <IoIosArrowDown
                                className={`text-white/30 transition-transform duration-200 shrink-0 ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                                size={12}
                              />
                            )}
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && !isCollapsed && (
                              <motion.div
                                key="submenu"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.2,
                                  ease: "easeInOut",
                                }}
                                className="overflow-hidden"
                              >
                                <div className="ml-[18px] pl-3 border-l border-white/10 mt-1 mb-1.5 flex flex-col gap-0.5">
                                  {(menuLink.children || [])
                                    .filter((child) =>
                                      isChildVisible(child, menuLink),
                                    )
                                    .map((child, subIndex) => {
                                      const isChildItemActive =
                                        location.pathname === child.link;
                                      const showNegativesPending =
                                        child.link === "/admin/negatives" &&
                                        negativePendingCount > 0;
                                      const showTicketsPending =
                                        child.link === "/admin/support" &&
                                        ticketsPendingCount > 0;
                                      return (
                                        <MenuLink
                                          item={child}
                                          key={subIndex}
                                          onClick={() =>
                                            handleToggleSidebar(
                                              formatTitle(child.name),
                                            )
                                          }
                                          className={`group flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] transition-all duration-150 ${
                                            isChildItemActive
                                              ? "text-white font-semibold"
                                              : "text-white/45 hover:text-white/85 hover:bg-white/[0.04]"
                                          }`}
                                          style={
                                            isChildItemActive
                                              ? {
                                                  background:
                                                    "rgba(59,130,246,0.10)",
                                                }
                                              : {}
                                          }
                                        >
                                          {child.icon && (
                                            <Icon
                                              className={`text-[16px] shrink-0 transition-colors ${
                                                isChildItemActive
                                                  ? "menu-accent-text-soft"
                                                  : "text-white/30 group-hover:text-white/55"
                                              }`}
                                            >
                                              {child.icon}
                                            </Icon>
                                          )}
                                          <span className="truncate flex-1">
                                            {child.name}
                                          </span>
                                          {showNegativesPending && (
                                            <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                                              {negativePendingCount}
                                            </span>
                                          )}
                                          {showTicketsPending && (
                                            <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
                                              {ticketsPendingCount}
                                            </span>
                                          )}
                                        </MenuLink>
                                      );
                                    })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            )}
          </div>

          {/* O cartão de perfil que ficava aqui saiu: o mesmo avatar, nome,
              e-mail e acesso ao perfil agora estão no menu da conta, na barra
              de cima. Dois pontos de entrada para a mesma coisa, um deles sem
              as ações (contrato, LGPD, sair), só dividia a atenção. */}
        </div>
      </aside>

      {/* ── Top bar ── */}
      <div
        className="mt-0 transition-all duration-300"
        style={{ marginLeft: `${contentMarginLeft}px`, width: contentWidth }}
      >
        <div className="container mx-auto">
          <div
            className="mainbar px-5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="flex justify-between items-center h-full">
              <div className="flex items-center gap-3">
                <button
                  ref={menuBtnRef}
                  type="button"
                  onClick={() => handleToggleSidebar()}
                  className="inline-flex items-center p-2 text-white/50 hover:text-white rounded-lg sm:hidden focus:outline-none transition"
                >
                  <span className="sr-only">Abrir sidebar</span>
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      fillRule="evenodd"
                      d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                    />
                  </svg>
                </button>
                <h2 className="font-semibold text-white text-[15px] tracking-tight">
                  {titlePage}
                </h2>
              </div>

              <div className="flex items-center gap-3">
                {canSeeNotificationCenter && (
                  <NotificationCenter
                    notifications={systemNotifications}
                    unreadCount={notificationsData?.unreadCount || 0}
                    isLoading={isLoadingNotifications}
                    onMarkAsRead={markNotificationAsRead}
                    onMarkAllAsRead={markAllNotificationsAsRead}
                    onOpenAsyncQueryResult={openAsyncQueryResult}
                  />
                )}

                {/* O "Olá, Fulano" que ficava solto ao lado virou o próprio
                    gatilho: antes o avatar era um círculo sem moldura, sem
                    seta e sem hover — nada dizia que abria menu, e o nome
                    repetido ao lado só ocupava espaço. O cartão segue o mesmo
                    padrão do rodapé da sidebar. */}
                <Dropdown
                  arrowIcon={false}
                  inline
                  className="z-50 w-64"
                  label=""
                  renderTrigger={() => (
                    <button
                      type="button"
                      aria-label={`Conta de ${userDisplayName}`}
                      className="group flex items-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] px-2 py-1.5 transition-colors hover:border-white/10 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                    >
                      <Avatar
                        rounded
                        size="sm"
                        img={user?.avatar || undefined}
                      />
                      <div className="hidden min-w-0 text-left md:block">
                        <p className="truncate text-[12px] font-semibold leading-tight text-white/85">
                          {userDisplayName}
                        </p>
                        <p className="truncate text-[10px] leading-tight text-white/35">
                          {rotuloDoPerfil || user?.email}
                        </p>
                      </div>
                      <HiChevronDown
                        size={14}
                        className="shrink-0 text-white/30 transition-colors group-hover:text-white/70"
                      />
                    </button>
                  )}
                >
                  <Dropdown.Header>
                    <div className="flex items-center gap-3">
                      <Avatar
                        rounded
                        size="md"
                        img={user?.avatar || undefined}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {userDisplayName}
                        </p>
                        <p className="truncate text-xs font-normal text-gray-500">
                          {user?.email}
                        </p>
                        {rotuloDoPerfil && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-100">
                            {rotuloDoPerfil}
                          </span>
                        )}
                      </div>
                    </div>
                  </Dropdown.Header>

                  <Dropdown.Item
                    icon={HiOutlineUserCircle}
                    as={Link}
                    to="/profile"
                    onClick={() => setTitlePage("Meu Perfil")}
                  >
                    Meu perfil
                  </Dropdown.Item>

                  {canSeeContract && (
                    <Dropdown.Item
                      icon={HiOutlineDocumentText}
                      onClick={() => setIsOpenModalContract(true)}
                    >
                      Meu contrato
                    </Dropdown.Item>
                  )}

                  <Dropdown.Item
                    icon={HiOutlineShieldCheck}
                    as={Link}
                    to="/lgpd"
                    onClick={() => setTitlePage("Aviso LGPD")}
                  >
                    {/* era "LGDP" */}
                    Aviso LGPD
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item
                    icon={HiOutlineArrowRightOnRectangle}
                    onClick={removeToken}
                    className="text-rose-600 hover:bg-rose-50 focus:bg-rose-50"
                  >
                    Sair
                  </Dropdown.Item>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />
      <ModalContract
        isOpen={isOpenModalContract}
        onClose={() => setIsOpenModalContract(false)}
      />
      {/* Espera o aceite dos termos: o ModalTerms abre antes e os dois não se
          empilham. Na personificação não abre — fechar marca como lido, e
          quem leria seria o suporte, não o cliente. */}
      <ImportantNotificationsModal
        userId={user?.id}
        enabled={
          canSeeNotificationCenter &&
          Boolean(user?.Company?.termsAcceptedAt) &&
          !isImpersonating()
        }
        onOpenAsyncQueryResult={openAsyncQueryResult}
      />
      {collapsedTooltip}
    </>
  );
};

export default Sidebar;
